import React, { useEffect, useRef, useState } from 'react';
import { 
    Box, 
    Typography, 
    TextField, 
    IconButton, 
    Paper, 
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Divider,
    Chip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupIcon from '@mui/icons-material/Group';
import { formatDistanceToNow } from 'date-fns';
import { auth } from '../../firebaseConfig';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { onMessageListener } from '../../services/notificationService';

const ChatWindow = ({ 
    newMessage, 
    setNewMessage, 
    onSendMessage, 
    selectedChat, 
    users,
    onSelectChat 
}) => {
    const [messages, setMessages] = useState([]);
    const messagesEndRef = useRef(null);
    const isMobile = window.innerWidth < 600;

    useEffect(() => {
        if (!selectedChat) return;

        const q = query(
            collection(db, 'messages'),
            where('chatId', '==', selectedChat.id),
            orderBy('timestamp', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const messagesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            console.log('Fetched messages:', messagesList);
            setMessages(messagesList);
        });

        return () => unsubscribe();
    }, [selectedChat]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        // Listen for incoming messages
        onMessageListener()
            .then((payload) => {
                if (payload) {
                    // Show notification if the app is in background
                    if (document.hidden) {
                        const notificationTitle = payload.notification.title;
                        const notificationOptions = {
                            body: payload.notification.body,
                            icon: '/logo192.png',
                            badge: '/logo192.png',
                            data: payload.data
                        };
                        
                        if (Notification.permission === 'granted') {
                            new Notification(notificationTitle, notificationOptions);
                        }
                    }
                }
            })
            .catch((err) => console.error('Error in onMessageListener:', err));
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const getParticipant = () => {
        if (selectedChat.isGroup) {
            return {
                displayName: selectedChat.groupName,
                isGroup: true
            };
        }
        const otherParticipantId = selectedChat.participants.find(id => id !== auth.currentUser?.uid);
        return users.find(user => user.id === otherParticipantId);
    };

    const participant = getParticipant();

    return (
        <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            bgcolor: '#f5f5f5',
            padding: '16px'
        }}>
            {/* Chat Header */}
            <Paper elevation={0} sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                bgcolor: 'white'
            }}>
                {isMobile && (
                    <IconButton 
                        onClick={() => onSelectChat(null)}
                        sx={{ mr: 1 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Avatar sx={{ mr: 2 }}>
                    {participant.isGroup ? <GroupIcon /> : participant?.displayName?.charAt(0)}
                </Avatar>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {participant?.displayName || 'Unknown User'}
                        </Typography>
                        {participant.isGroup && (
                            <Chip 
                                label="Group" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                            />
                        )}
                    </Box>
                    {!participant.isGroup && (
                        <Typography variant="caption" color="text.secondary">
                            {participant?.unitNumber || 'No unit number'}
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/* Messages */}
            <Box sx={{ 
                flex: 1, 
                overflow: 'auto', 
                p: 2,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <List sx={{ width: '100%' }}>
                    {messages.map((message) => {
                        const isOwnMessage = message.senderId === auth.currentUser?.uid;
                        return (
                            <ListItem 
                                key={message.id}
                                sx={{ 
                                    justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                                    mb: 1
                                }}
                            >
                                <Box sx={{ 
                                    maxWidth: '70%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: isOwnMessage ? 'flex-end' : 'flex-start'
                                }}>
                                    {!isOwnMessage && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                            <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                                                {message.senderName?.charAt(0) || 'U'}
                                            </Avatar>
                                            <Typography variant="caption" color="text.secondary">
                                                {message.senderName}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: isOwnMessage ? '#1976d2' : 'white',
                                            color: isOwnMessage ? 'white' : 'text.primary'
                                        }}
                                    >
                                        <Typography variant="body1">
                                            {message.text}
                                        </Typography>
                                    </Paper>
                                    <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {message.timestamp 
                                            ? formatDistanceToNow(message.timestamp.toDate(), { addSuffix: true })
                                            : 'Just now'}
                                    </Typography>
                                </Box>
                            </ListItem>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </List>
            </Box>

            {/* Message Input */}
            <Paper 
                elevation={0}
                sx={{ 
                    p: 2, 
                    borderTop: '1px solid #e0e0e0',
                    bgcolor: 'white'
                }}
            >
                <form onSubmit={(e) => {
                    e.preventDefault();
                    onSendMessage();
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            sx={{ mr: 1 }}
                        />
                        <IconButton 
                            type="submit" 
                            color="primary"
                            disabled={!newMessage.trim()}
                        >
                            <SendIcon />
                        </IconButton>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default ChatWindow; 